import React, { useState } from 'react';
import { Smartphone, Download, Bell, Monitor, Apple, ChevronDown, ChevronUp, CheckCircle } from 'lucide-react';
import NotificationSettings from '@/components/NotificationSettings';
import { Button } from '@/components/ui/button';

const PWAGuide = () => {
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-2xl mb-4">
          <Smartphone className="text-blue-600" size={32} />
        </div>
        <h1 className="text-2xl font-bold text-slate-900">Mipay Mobile App</h1>
        <p className="text-slate-500 mt-1">Install Mipay on your device for the best experience</p>
      </div>

      {/* Installation Status */}
      {isStandalone && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
          <div className="flex items-center gap-3">
            <CheckCircle className="text-emerald-600" size={24} />
            <div>
              <h3 className="font-semibold text-emerald-900">App Installed!</h3>
              <p className="text-sm text-emerald-700">You're using Mipay as an installed app.</p>
            </div>
          </div>
        </div>
      )}

      {/* Install Instructions - All Devices */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 bg-blue-50 border-b border-blue-100">
          <div className="flex items-center gap-3">
            <Download className="text-blue-600" size={24} />
            <div>
              <h3 className="font-semibold text-blue-900">Install the App</h3>
              <p className="text-sm text-blue-700">Choose your device below</p>
            </div>
          </div>
        </div>

        {/* iPhone/iPad Section */}
        <div className="border-b border-slate-100">
          <button
            onClick={() => toggleSection('ios')}
            className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <span className="text-2xl">📱</span>
              </div>
              <div className="text-left">
                <h4 className="font-medium text-slate-900">iPhone / iPad</h4>
                <p className="text-sm text-slate-500">Safari browser required</p>
              </div>
            </div>
            {expandedSection === 'ios' ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
          </button>
          
          {expandedSection === 'ios' && (
            <div className="px-4 pb-4 bg-slate-50">
              <ol className="space-y-4 text-sm">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
                  <div>
                    <p className="font-medium text-slate-900">Open Safari browser</p>
                    <p className="text-slate-500">This only works in Safari (not Chrome)</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                  <div>
                    <p className="font-medium text-slate-900">Go to mipay.uk</p>
                    <p className="text-slate-500">Type mipay.uk in the address bar</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                  <div>
                    <p className="font-medium text-slate-900">Tap the Share button</p>
                    <p className="text-slate-500">Square with arrow (↑) at the bottom of Safari</p>
                    <div className="mt-2 inline-flex items-center gap-2 bg-white border border-slate-200 px-3 py-2 rounded-lg">
                      <span className="text-blue-600 text-lg">↑</span>
                      <span className="text-slate-600">Share</span>
                    </div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">4</span>
                  <div>
                    <p className="font-medium text-slate-900">Tap "Add to Home Screen"</p>
                    <p className="text-slate-500">Scroll down in the share menu to find it</p>
                    <div className="mt-2 inline-flex items-center gap-2 bg-white border border-slate-200 px-3 py-2 rounded-lg">
                      <span className="text-slate-600">➕</span>
                      <span className="text-slate-600">Add to Home Screen</span>
                    </div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">5</span>
                  <div>
                    <p className="font-medium text-slate-900">Tap "Add"</p>
                    <p className="text-slate-500">Mipay icon will appear on your home screen!</p>
                  </div>
                </li>
              </ol>
            </div>
          )}
        </div>

        {/* Android Section */}
        <div className="border-b border-slate-100">
          <button
            onClick={() => toggleSection('android')}
            className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <span className="text-2xl">🤖</span>
              </div>
              <div className="text-left">
                <h4 className="font-medium text-slate-900">Android</h4>
                <p className="text-sm text-slate-500">Samsung, Xiaomi, Huawei, etc.</p>
              </div>
            </div>
            {expandedSection === 'android' ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
          </button>
          
          {expandedSection === 'android' && (
            <div className="px-4 pb-4 bg-slate-50">
              <ol className="space-y-4 text-sm">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
                  <div>
                    <p className="font-medium text-slate-900">Open Chrome browser</p>
                    <p className="text-slate-500">Use Google Chrome for best results</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                  <div>
                    <p className="font-medium text-slate-900">Go to mipay.uk</p>
                    <p className="text-slate-500">Type mipay.uk in the address bar</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                  <div>
                    <p className="font-medium text-slate-900">Tap the Menu (⋮)</p>
                    <p className="text-slate-500">Three dots at the top right corner</p>
                    <div className="mt-2 inline-flex items-center gap-2 bg-white border border-slate-200 px-3 py-2 rounded-lg">
                      <span className="text-slate-600 text-xl">⋮</span>
                      <span className="text-slate-600">Menu</span>
                    </div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-bold">4</span>
                  <div>
                    <p className="font-medium text-slate-900">Tap "Install app" or "Add to Home screen"</p>
                    <p className="text-slate-500">Look for the install option in the menu</p>
                    <div className="mt-2 inline-flex items-center gap-2 bg-white border border-slate-200 px-3 py-2 rounded-lg">
                      <Download size={18} className="text-slate-600" />
                      <span className="text-slate-600">Install app</span>
                    </div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-bold">5</span>
                  <div>
                    <p className="font-medium text-slate-900">Tap "Install"</p>
                    <p className="text-slate-500">Mipay icon will appear on your home screen!</p>
                  </div>
                </li>
              </ol>
            </div>
          )}
        </div>

        {/* Desktop Section */}
        <div>
          <button
            onClick={() => toggleSection('desktop')}
            className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Monitor className="text-purple-600" size={20} />
              </div>
              <div className="text-left">
                <h4 className="font-medium text-slate-900">Desktop / Laptop</h4>
                <p className="text-sm text-slate-500">Chrome or Edge browser</p>
              </div>
            </div>
            {expandedSection === 'desktop' ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
          </button>
          
          {expandedSection === 'desktop' && (
            <div className="px-4 pb-4 bg-slate-50">
              <ol className="space-y-4 text-sm">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
                  <div>
                    <p className="font-medium text-slate-900">Look for the install icon</p>
                    <p className="text-slate-500">In the address bar (right side), you'll see a download/install icon</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                  <div>
                    <p className="font-medium text-slate-900">Click "Install"</p>
                    <p className="text-slate-500">Mipay will open as a standalone app!</p>
                  </div>
                </li>
              </ol>
            </div>
          )}
        </div>
      </div>

      {/* Push Notification Settings */}
      <NotificationSettings />

      {/* Benefits */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Why Install the App?</h3>
        <div className="grid gap-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
              <Smartphone className="text-blue-600" size={18} />
            </div>
            <div>
              <h4 className="font-medium text-slate-900">Quick Access</h4>
              <p className="text-sm text-slate-500">Launch Mipay instantly from your home screen</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="p-2 bg-amber-100 rounded-lg flex-shrink-0">
              <Bell className="text-amber-600" size={18} />
            </div>
            <div>
              <h4 className="font-medium text-slate-900">Push Notifications</h4>
              <p className="text-sm text-slate-500">Get alerts for timesheet approvals & cash advances</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg flex-shrink-0">
              <CheckCircle className="text-emerald-600" size={18} />
            </div>
            <div>
              <h4 className="font-medium text-slate-900">Full Screen Experience</h4>
              <p className="text-sm text-slate-500">No browser bars - feels like a native app</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAGuide;
